import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Phone, ArrowRight } from "lucide-react";
import { buildWhatsAppLink, SITE } from "@/lib/site";

export const Footer = () => {
  return (
    <footer className="bg-ivory" data-testid="site-footer">
      {/* Urgency Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="bg-charcoal text-ivory"
        data-testid="footer-urgency-banner"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10 md:py-14 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="text-xs uppercase tracking-[0.22em] text-ivory/55">
              A quiet note
            </div>
            <p className="mt-3 font-serif text-2xl md:text-3xl leading-snug">
              Because we only host{" "}
              <span className="italic text-sage-200">three families</span> at a time,
              villas are booked months in advance.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <a
              href={buildWhatsAppLink("availability")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-ivory text-charcoal px-6 py-3.5 rounded-full text-sm font-medium hover:bg-sage hover:text-ivory transition-colors"
              data-testid="footer-cta"
            >
              Reserve your dates
              <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </motion.div>

      {/* Main footer */}
      <div className="border-t border-sage/15">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 md:py-20 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <img
              src={SITE.logoLight}
              alt="Kakkanat Ayurveda"
              className="h-20 w-auto object-contain -ml-2"
              style={{ mixBlendMode: "multiply" }}
              draggable={false}
            />
            <p className="mt-5 text-base text-charcoal/65 max-w-md leading-relaxed">
              Authentic Ayurvedic Postnatal Care, Prasava Raksha. Three private
              villas, doctor-led, rooted in Kerala hospitality.
            </p>

            <div className="mt-8">
              <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
                Explore
              </div>
              <ul className="mt-4 space-y-2.5" data-testid="footer-explore-links">
                <li>
                  <Link
                    to="/postnatal-care-palakkad-thrissur"
                    className="inline-flex items-center gap-2 text-sm text-charcoal/75 hover:text-sage transition-colors"
                    data-testid="footer-link-location"
                  >
                    Postnatal care near Palakkad & Thrissur
                  </Link>
                </li>
                <li>
                  <Link
                    to="/faq"
                    className="inline-flex items-center gap-2 text-sm text-charcoal/75 hover:text-sage transition-colors"
                    data-testid="footer-link-faq"
                  >
                    Prasava Raksha FAQ
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog/what-is-prasava-raksha"
                    className="inline-flex items-center gap-2 text-sm text-charcoal/75 hover:text-sage transition-colors"
                    data-testid="footer-link-blog"
                  >
                    What is Prasava Raksha? A simple guide
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
              Visit
            </div>
            <div className="mt-4 flex items-start gap-3 text-charcoal/75">
              <MapPin size={18} strokeWidth={1.5} className="mt-1 text-sage shrink-0" />
              <p className="text-sm leading-relaxed" data-testid="footer-address">
                {SITE.brandFull},
                <br />
                {SITE.address}.
              </p>
            </div>
            <div className="mt-5 rounded-2xl overflow-hidden border border-sage/15">
              <iframe
                src="https://www.google.com/maps?q=Kakkanat%20Ayurveda%20Postnatal%20care%20centre%20%2F%20Prasavaraksha%20centre%2C%20Paruthipra%2C%20Post%20Office%20Rd%2C%20Shoranur%2C%20Kerala%20679121&output=embed"
                title="Map showing Kakkanat Ayurveda Postnatal Care Centre, Paruthipra, Shoranur, Kerala"
                className="block w-full h-44 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="footer-map"
              />
            </div>
            <a
              href="https://maps.app.goo.gl/i3WyDCZjckCY6SGM8"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 border border-sage/40 text-charcoal px-5 py-2.5 rounded-full text-sm font-medium hover:bg-sage hover:text-ivory hover:border-sage transition-colors"
              data-testid="footer-directions-cta"
            >
              <MapPin size={15} strokeWidth={1.75} />
              Get Directions
            </a>
          </div>

          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
              Speak with us
            </div>
            <div className="mt-4 flex items-start gap-3 text-charcoal/75">
              <Phone size={18} strokeWidth={1.5} className="mt-1 text-sage shrink-0" />
              <div>
                <a
                  href={buildWhatsAppLink("default")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-base font-medium text-charcoal hover:text-sage transition-colors"
                  data-testid="footer-whatsapp"
                >
                  {SITE.whatsappDisplay}
                </a>
                <div className="text-sm text-charcoal/55 mt-1">
                  WhatsApp · 9am – 8pm IST
                </div>
              </div>
            </div>
            <a
              href={buildWhatsAppLink("book")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 border border-sage/40 text-charcoal px-5 py-3 rounded-full text-sm font-medium hover:bg-sage hover:text-ivory hover:border-sage transition-colors"
              data-testid="footer-book-cta"
            >
              Book your villa
            </a>
          </div>
        </div>

        <div className="border-t border-sage/15">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-6 text-xs text-charcoal/45 leading-relaxed" data-testid="footer-seo-line">
            Kakkanat Postnatal Care - Ayurvedic Postnatal Care Centre in
            Palakkad, Kerala. Paruthipra, Shoranur, Palakkad, Kerala 679121.
          </div>
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-4 text-xs text-charcoal/45 leading-relaxed space-y-0.5" data-testid="footer-legal-block">
            <div>Kakkanat Ayurveda Ashramam</div>
            <div>Proprietor: KAKKANAT BALAKRISHNAN VALSAKUMAR</div>
            <div>
              1/29,30,31 and 32, Kakkanat Building, Paruthipra, Shoranur-1 PO, Palakkad, Kerala 679121
            </div>
            <div>GSTIN: 32ABNPV6078K1ZX</div>
          </div>
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-charcoal/55">
            <span>
              © {new Date().getFullYear()} {SITE.brandFull}. All rights reserved.
            </span>
            <span className="italic font-serif text-sage-700 text-sm">
              Prasava Raksha. Care, the way it was meant to be.
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
